/** @jsx jsx */
import PropTypes from 'prop-types';
import { jsx } from '@emotion/core';
import { Text } from '@sitecore-jss/sitecore-jss-react';
import forEach from 'lodash/forEach';
import Picture from '../../../ui/Picture';
import { Breakpoints } from '../../../../styles/Grid/GridSizes';
import { SolutionsInTheAreaKeys as dict } from '../../../../utils/dictionarykeys';
import { Container, Row, Column } from '../../../../styles/Grid';
import { CLOUDINARY_BASE_VALUES } from '../../../../utils/constants';
import applyPluralRule from '../../../../utils/pluralRule';
import { SafePureComponent } from '../../../../utils/SafePureComponent';
import * as styles from './index.styles.js';

const contactImageSize = {
  ...CLOUDINARY_BASE_VALUES,
  aspectRatio: '1:1',
  height: 48,
  width: 48,
};

const contactImageSizes = {
  [Breakpoints.sm]: contactImageSize,
  [Breakpoints.md]: contactImageSize,
  [Breakpoints.lg]: contactImageSize,
  [Breakpoints.xl]: contactImageSize,
};

const emptyValue = {
  value: '',
};

const defaultData = {
  availableLocation: 0,
  ContactIcon: emptyValue,
  ContactPhrase: emptyValue,
  CountryPhone: emptyValue,
  Headline: emptyValue,
  Subtitle: emptyValue,
  SubtitleVariation: emptyValue,
  totalLocation: 0,
};

class Hero extends SafePureComponent {
  /**
   * Replaces the given values in a string. Values to be replace must be
   * wrapped in curly braces
   * str: We’ve suggested {availableLocation}.
   * values: {availableLocation: 1}
   * outputs to:
   * ["We’ve suggested ", "<span>1 location</span>", "."]
   * str: We’ve suggested {availableLocation}.
   * values: {availableLocation: 3}
   * output:
   * ["We’ve suggested ", "<span>3 locations</span>", "."]
   * @param {string} str string to be modified
   * @param {object} values to be replaced in the string
   * @return {Array} Strings and React.node type elements
   */
  replaceLocationCount(str, values) {
    const pipes = '||';

    // Updates string with characters that are easier for splitting
    forEach(values, function (_value, key) {
      const regex = new RegExp(`{${key}}`, 'gi');
      str = str.replace(regex, `${pipes}${key}${pipes}`);
    });

    // Splits the string by the pipes and then loops through the items and if
    // an item matches a key from within the values object then the content is
    // updated to display the count and proper location(s) text. And these are
    // wrapped in spans so react can render it later on.
    return str.split(pipes).map((item, key) => {
      if (values[item]) {
        const label = applyPluralRule({
          dictionary: dict.LocationsLabel,
          quantity: values[item],
        });

        return (
          <span key={key}>
            {values[item]} {label}
          </span>
        );
      }

      return item;
    });
  }

  getSubtitle() {
    const {
      availableLocation,
      Subtitle,
      SubtitleVariation,
      totalLocation,
    } = this.props;
    const subtitle = totalLocation > 3 ? SubtitleVariation : Subtitle;

    return this.replaceLocationCount(subtitle.value, {
      availableLocation,
      totalLocation,
    });
  }

  safeRender() {
    const { ContactIcon, ContactPhrase, CountryPhone, Headline } = this.props;

    return (
      <header css={styles.hero}>
        <Container>
          <Row>
            <Column cols={{ sm: 6, md: 7, lg: 8, xl: 7 }}>
              <h1 css={styles.headline}>
                <Text field={Headline} />
              </h1>
            </Column>
          </Row>
          <Row css={styles.subHeadline}>
            <Column cols={{ sm: 6, md: 7, lg: 8, xl: 7 }}>
              <p css={styles.subHeadlineCopy}>{this.getSubtitle()}</p>
            </Column>
            <Column
              cols={{ sm: 6, md: 8, lg: 4, xl: 5 }}
              css={styles.contactInformation}
            >
              <div css={styles.contactInformationThumbnailWrapper}>
                <Picture
                  image={ContactIcon}
                  imageCss={styles.contactInformationThumbnail}
                  imageSizes={contactImageSizes}
                  instantLoad
                />
              </div>
              <div css={styles.contactInformationContactInfo}>
                <h4 css={styles.contactInformationCopy}>
                  <Text field={ContactPhrase} />
                </h4>
                <a
                  href={`tel: ${CountryPhone}`}
                  css={styles.contactInformationNumber}
                >
                  <Text field={CountryPhone} />
                </a>
              </div>
            </Column>
          </Row>
        </Container>
      </header>
    );
  }
}

const objectValueType = {
  value: PropTypes.string,
};

Hero.displayName = 'SolutionBuilderResults/Hero';

Hero.defaultProps = {
  ...defaultData,
};

Hero.propTypes = {
  availableLocation: PropTypes.number,
  ContactIcon: PropTypes.shape({
    src: PropTypes.string,
  }),
  ContactPhrase: PropTypes.shape(objectValueType),
  CountryPhone: PropTypes.shape(objectValueType),
  Headline: PropTypes.shape(objectValueType),
  Subtitle: PropTypes.shape(objectValueType),
  SubtitleVariation: PropTypes.shape(objectValueType),
  totalLocation: PropTypes.number,
};

export default Hero;
